package com.milta.feature.aicredit.presentation

import com.milta.core.mvi.MviViewModel
import com.milta.core.network.ApiException
import com.milta.feature.aicredit.domain.entity.AdminAccountAiCredit
import com.milta.feature.aicredit.domain.entity.AdminAiCreditHistoryEntry
import com.milta.feature.aicredit.domain.usecase.GetAccountAiCreditHistoryUseCase
import com.milta.feature.aicredit.domain.usecase.GetAccountAiCreditUseCase
import com.milta.feature.aicredit.domain.usecase.GetAiCreditSettingsUseCase
import com.milta.feature.aicredit.domain.usecase.ReplaceRemainingAiCreditUseCase
import kotlinx.coroutines.Job
import kotlinx.coroutines.launch

/** Server cap on a replaced remaining credit (R11): 0 to 100 €. */
const val AI_CREDIT_MAX_REMAINING_EUR = 100.0

/** "Crédit restant remplacé : [fromEur] → [toEur]" banner, right after a replacement (R11). */
data class AiCreditReplacement(val fromEur: Double, val toEur: Double)

data class AdminAccountAiCreditState(
    val isLoading: Boolean = true,
    /** "not_found" | "server_error" | "offline". */
    val error: String? = null,
    val data: AdminAccountAiCredit? = null,
    /** Low-credit threshold of the settings, for the "Crédit bas · sous X %" pill; null if unread. */
    val lowThresholdPercent: Int? = null,
    val remainingInput: String = "",
    /** Empty or above [AI_CREDIT_MAX_REMAINING_EUR] — nothing sent. */
    val remainingInputInvalid: Boolean = false,
    val isReplacing: Boolean = false,
    val lastReplacement: AiCreditReplacement? = null,
    /** "validation" | "server_error" | "offline". */
    val replaceError: String? = null,
    val history: AiCreditHistoryState<AdminAiCreditHistoryEntry> = AiCreditHistoryState(),
)

sealed interface AdminAccountAiCreditIntent {
    data object Load : AdminAccountAiCreditIntent
    data object LoadMoreHistory : AdminAccountAiCreditIntent
    data class UpdateRemainingInput(val value: String) : AdminAccountAiCreditIntent
    data object ReplaceRemaining : AdminAccountAiCreditIntent
}

/**
 * MIL-98 — Admin "Fiche utilisateur", block "Crédit IA" (A2): the account's own credit in euros,
 * "Remplacer le crédit restant" for the current period only (R11) and the paged history with tokens
 * and cost (R13). Replacing the credit does not touch the history, so it is not reloaded.
 */
class AdminAccountAiCreditViewModel(
    private val accountId: String,
    private val getAccountCredit: GetAccountAiCreditUseCase,
    private val getAccountHistory: GetAccountAiCreditHistoryUseCase,
    private val replaceRemaining: ReplaceRemainingAiCreditUseCase,
    private val getSettings: GetAiCreditSettingsUseCase,
) : MviViewModel<AdminAccountAiCreditState, AdminAccountAiCreditIntent>(AdminAccountAiCreditState()) {

    init {
        onIntent(AdminAccountAiCreditIntent.Load)
    }

    override fun onIntent(intent: AdminAccountAiCreditIntent) {
        when (intent) {
            AdminAccountAiCreditIntent.Load -> load()
            AdminAccountAiCreditIntent.LoadMoreHistory -> loadMore()
            is AdminAccountAiCreditIntent.UpdateRemainingInput -> setState {
                copy(
                    remainingInput = AiCreditFormat.sanitizeAmountInput(intent.value, maxDecimals = 2, maxWholeDigits = 3),
                    remainingInputInvalid = false,
                    replaceError = null,
                )
            }
            AdminAccountAiCreditIntent.ReplaceRemaining -> replace()
        }
    }

    /** Bumped by [load]: a page answer from before a reset is ignored (no skipped page). */
    private var historyGeneration = 0
    private var historyJob: Job? = null

    private fun load() {
        historyGeneration++
        historyJob?.cancel()
        setState { copy(isLoading = true, error = null, history = AiCreditHistoryState()) }
        scope.launch {
            getAccountCredit(accountId).fold(
                onSuccess = { data -> setState { copy(isLoading = false, data = data) } },
                onFailure = { e -> setState { copy(isLoading = false, error = errorCode(e)) } },
            )
        }
        scope.launch {
            getSettings().onSuccess { settings -> setState { copy(lowThresholdPercent = settings.lowThresholdPercent) } }
        }
        loadMore()
    }

    private fun loadMore() {
        val history = currentState.history
        if (history.isLoadingPage || !history.hasMore) return
        setState { copy(history = history.copy(isLoadingPage = true, error = null)) }
        val generation = historyGeneration
        historyJob = scope.launch {
            val result = getAccountHistory(accountId, history.nextPage, AI_CREDIT_HISTORY_PAGE_SIZE)
            if (generation != historyGeneration) return@launch
            result.fold(
                onSuccess = { page -> setState { copy(history = this.history.withPage(page) { it.id }) } },
                onFailure = { e -> setState { copy(history = this.history.withError(e)) } },
            )
        }
    }

    private fun replace() {
        val current = currentState.data ?: return
        if (currentState.isReplacing) return
        val value = AiCreditFormat.parseDecimal(currentState.remainingInput)
        if (value == null || value < 0 || value > AI_CREDIT_MAX_REMAINING_EUR) {
            setState { copy(remainingInputInvalid = true) }
            return
        }
        val previous = current.credit.remainingEur
        setState { copy(isReplacing = true, replaceError = null, lastReplacement = null) }
        scope.launch {
            replaceRemaining(accountId, value).fold(
                onSuccess = { credit ->
                    setState {
                        copy(
                            isReplacing = false,
                            // The PUT returns the whole AdminAiCreditDto, period summary included.
                            data = data?.copy(credit = credit),
                            remainingInput = "",
                            lastReplacement = AiCreditReplacement(previous, credit.remainingEur),
                        )
                    }
                },
                onFailure = { e ->
                    val code = if (e is ApiException && e.statusCode == HTTP_BAD_REQUEST) "validation" else errorCode(e)
                    setState { copy(isReplacing = false, replaceError = code) }
                },
            )
        }
    }

    private fun errorCode(e: Throwable) = when {
        e is ApiException && e.statusCode == HTTP_NOT_FOUND -> "not_found"
        e is ApiException -> "server_error"
        else -> "offline"
    }

    private companion object {
        const val HTTP_BAD_REQUEST = 400
        const val HTTP_NOT_FOUND = 404
    }
}
